import React, { Component } from "react";
import axios from "../../utils/axiosInstance";
import Swal from "sweetalert2";

class Faqs extends Component {
  state = {
    faqs: [],
    formData: {
      id: null,
      faq_type: "",
      faq_question: "",
      faq_answer: "",
      faq_status: "1",
      faq_date: "",
    },
    deleteId: null,
    error: "",
    loading: false,
    submitting: false,
    modalMode: "add",
  };

  componentDidMount() {
    this.fetchFaqs();

    const modalEl = document.getElementById("faqModal");
    if (modalEl) {
      modalEl.addEventListener("hidden.bs.modal", () => {
        this.resetForm();
      });
    }
  }

  /** ✅ Fetch all FAQs */
  fetchFaqs = async () => {
    this.setState({ loading: true, error: "" });
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get("/faqs", {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = res.data?.data || [];
      this.setState({ faqs: Array.isArray(data) ? data : [] });
    } catch (err) {
      this.setState({
        error:
          err.response?.data?.message ||
          "Failed to load FAQs. Please try again.",
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  /** ✅ Reset form */
  resetForm = () => {
    this.setState({
      formData: {
        id: null,
        faq_type: "",
        faq_question: "",
        faq_answer: "",
        faq_status: "1",
        faq_date: "",
      },
      error: "",
      submitting: false,
      modalMode: "add",
    });
  };

  /** ✅ Input change */
  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      formData: {
        ...prev.formData,
        [name]: value,
      },
    }));
  };

  /** ✅ Open modal */
  openModal = (mode = "add", faq = null) => {
    const modal = new window.bootstrap.Modal(
      document.getElementById("faqModal")
    );

    if (mode === "edit" && faq) {
      this.setState({
        modalMode: "edit",
        formData: {
          id: faq.id,
          faq_type: faq.faq_type || "",
          faq_question: faq.faq_question || "",
          faq_answer: faq.faq_answer || "",
          faq_status: faq.faq_status?.toString() ?? "1",
          faq_date: faq.faq_date || "",
        },
        error: "",
      });
    } else {
      this.resetForm();
    }

    modal.show();
  };

  /** ✅ Submit add/update */
  handleSubmit = async (e) => {
    e.preventDefault();
    const { formData, modalMode } = this.state;

    if (!formData.faq_type) {
      return this.setState({ error: "Admin ID is required." });
    }
    if (!formData.faq_question) {
      return this.setState({ error: "FAQ question is required." });
    }
    if (formData.faq_status === "") {
      return this.setState({ error: "Status is required." });
    }

    const data = new FormData();
    data.append("faq_type", formData.faq_type);
    data.append("faq_question", formData.faq_question);
    data.append("faq_answer", formData.faq_answer);
    data.append("faq_status", formData.faq_status);
    data.append("faq_date", formData.faq_date);

    this.setState({ submitting: true, error: "" });

    try {
      const token = localStorage.getItem("token");

      if (modalMode === "add") {
        await axios.post("/faqs", data, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
      } else {
        await axios.post(`/faqs/${formData.id}?_method=PUT`, data, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
      }

      await this.fetchFaqs();

      const modal = window.bootstrap.Modal.getInstance(
        document.getElementById("faqModal")
      );
      if (modal) modal.hide();

      Swal.fire({
        icon: "success",
        title:
          modalMode === "add"
            ? "FAQ added successfully!"
            : "FAQ updated successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      console.error(err);
      this.setState({
        error:
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Error saving FAQ.",
      });
    } finally {
      this.setState({ submitting: false });
    }
  };

  /** ✅ Confirm delete */
  confirmDelete = (id) => {
    this.setState({ deleteId: id });
    const modal = new window.bootstrap.Modal(
      document.getElementById("deleteModal")
    );
    modal.show();
  };

  /** ✅ Delete FAQ */
  handleDelete = async () => {
    const { deleteId } = this.state;
    if (!deleteId) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/faqs/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      await this.fetchFaqs();

      const modal = window.bootstrap.Modal.getInstance(
        document.getElementById("deleteModal")
      );
      if (modal) modal.hide();

      Swal.fire({
        icon: "success",
        title: "FAQ deleted successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Failed to delete FAQ.",
      });
    }
  };

  render() {
    const { faqs, formData, error, loading, submitting, modalMode } =
      this.state;

    return (
      <div className="pc-container">
        <div className="pc-content">
          <div className="row">
            <div className="col-lg-12">
              <div className="card shadow-none">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <ul className="breadcrumb mb-0">
                    <li className="breadcrumb-item">FAQs</li>
                    <li className="breadcrumb-item active">List</li>
                  </ul>

                  <button
                    type="button"
                    className="btn btn-light-warning"
                    onClick={() => this.openModal("add")}
                  >
                    + New FAQ
                  </button>
                </div>

                <div className="card-body">
                  {error && <div className="alert alert-danger">{error}</div>}

                  {loading ? (
                    <div className="text-center py-4">Loading...</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-bordered table-striped">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Type</th>
                            <th>Question</th>
                            <th>Answer</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {faqs.length > 0 ? (
                            faqs.map((faq, index) => (
                              <tr key={faq.id}>
                                <td>{index + 1}</td>
                                <td>{faq.faq_type}</td>
                                <td>{faq.faq_question}</td>
                                <td>
                                  {faq.faq_answer
                                    ? faq.faq_answer.length > 80
                                      ? faq.faq_answer.substring(0, 80) + "..."
                                      : faq.faq_answer
                                    : "N/A"}
                                </td>
                                <td>
                                  {faq.faq_status === "1" ||
                                  faq.faq_status === 1 ? (
                                    <span className="badge bg-success">
                                      Active
                                    </span>
                                  ) : (
                                    <span className="badge bg-danger">
                                      Inactive
                                    </span>
                                  )}
                                </td>
                                <td>
                                  <button
                                    className="btn btn-sm btn-light-success me-2"
                                    onClick={() => this.openModal("edit", faq)}
                                  >
                                    <i className="ph ph-note-pencil"></i>
                                  </button>

                                  <button
                                    className="btn btn-sm btn-light-danger"
                                    onClick={() => this.confirmDelete(faq.id)}
                                  >
                                    <i className="ph ph-trash"></i>
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="6" className="text-center">
                                No FAQs found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Add / Edit Modal */}
        <div className="modal fade" id="faqModal" tabIndex={-1}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <form onSubmit={this.handleSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">
                    {modalMode === "add" ? "Add FAQ" : "Edit FAQ"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                  ></button>
                </div>

                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Type</label>
                      <input
                        type="text"
                        name="faq_type"
                        className="form-control"
                        value={formData.faq_type}
                        onChange={this.handleChange}
                        required
                      />
                    </div>

                    {/* FAQ Status */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Status</label>
                      <select
                        name="faq_status"
                        className="form-select"
                        value={formData.faq_status}
                        onChange={this.handleChange}
                        required
                      >
                        <option value="1">Active</option>
                        <option value="0">Inactive</option>
                      </select>
                    </div>

                    {/* FAQ Question */}
                    <div className="col-md-12 mb-3">
                      <label className="form-label">FAQ Question</label>
                      <input
                        type="text"
                        name="faq_question"
                        className="form-control"
                        value={formData.faq_question}
                        onChange={this.handleChange}
                        required
                      />
                    </div>

                    {/* FAQ Answer */}
                    <div className="col-md-12 mb-3">
                      <label className="form-label">FAQ Answer</label>
                      <textarea
                        name="faq_answer"
                        className="form-control"
                        rows="6"
                        value={formData.faq_answer}
                        onChange={this.handleChange}
                      ></textarea>
                    </div>

                    {error && (
                      <div className="col-md-12">
                        <div className="alert alert-danger">{error}</div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light-danger"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="btn btn-light-primary"
                    disabled={submitting}
                  >
                    {submitting
                      ? "Saving..."
                      : modalMode === "add"
                      ? "Add"
                      : "Update"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Delete Modal */}
        <div className="modal fade" id="deleteModal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-danger">
              <div className="modal-body text-center p-4">
                <i className="ph ph-warning text-danger f-40 mb-3"></i>
                <h5>Are you sure?</h5>
                <p className="text-muted mb-4">This action cannot be undone.</p>

                <div className="d-flex justify-content-center gap-3">
                  <button
                    type="button"
                    className="btn btn-light-secondary"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="btn btn-light-danger"
                    onClick={this.handleDelete}
                  >
                    Yes, Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Faqs;
